import { CrewAvailability } from '../types/CrewAvailability'
import { londonDayKey, londonDaysInRange } from '@/lib/availability-dates'

type Availability = CrewAvailability[0]

/** Every period covering a London day, not just the first one that matches. */
/**
 * Days already covered by a period a new one would clash with: same kind, not denied, and not
 * the one being edited. Mirrors the guard in crew-api, so the date picker shows exactly what the
 * server is going to refuse rather than leaving the crew member to find out on save.
 */
export const busyDayKeys = (items: CrewAvailability, available: boolean, excludeIds: number[] = []): string[] => {
  const days = new Set<string>()

  items
    .filter(item => item.available === available && item.approved !== -1 && !excludeIds.includes(item.id))
    .forEach(item => {
      londonDaysInRange(item.from_date, item.to_date).forEach(day => days.add(day))
    })

  return Array.from(days)
}

export const availabilitiesOnDay = (items: CrewAvailability, day: string): CrewAvailability =>
  items.filter(item => londonDayKey(item.from_date) <= day && day <= londonDayKey(item.to_date))

// One palette for the month cells and the detail card. These were two separate copies that had
// already drifted: the cell said "iOS red" next to a grey, so an unavailable month looked dead
// rather than blocked out. Red now, matching the office scheduler.
export const availabilityColors = {
  denied: '#8E8E93',
  pending: '#8A2BE2',
  tentative: '#FF9500',
  available: '#34C759',
  unavailable: '#D14343',
} as const

export type AvailabilityStatus = keyof typeof availabilityColors

export const getAvailabilityStatus = (availability: Availability): AvailabilityStatus => {
  if (availability.approved === -1) return 'denied'
  if (!availability.approved) return 'pending'
  if (availability.type === 2) return 'tentative'
  return availability.available ? 'available' : 'unavailable'
}

export const getAvailabilityColor = (availability: Availability): string =>
  availabilityColors[getAvailabilityStatus(availability)]

// When several periods cover one day the cell can only be one colour, so show the one the crew
// member most needs to act on. Pending first (the office has not answered yet), then denied (they
// think they are off and are not), then the blocking states, then available.
const statusPriority: Record<AvailabilityStatus, number> = {
  pending: 0,
  denied: 1,
  unavailable: 2,
  tentative: 3,
  available: 4,
}

export const mostSignificantAvailability = (items: Availability[]): Availability | undefined =>
  items
    .slice()
    .sort((a, b) => statusPriority[getAvailabilityStatus(a)] - statusPriority[getAvailabilityStatus(b)])[0]

export const getAvailabilityLabel = (availability: Availability): string => {
  const base = availability.type === 2 ? 'Tentative' : availability.available ? 'Available' : 'Unavailable'

  if (availability.approved === -1) return `${base} (Denied)`
  if (!availability.approved) return `${base} (Pending)`
  return `${base} (Approved)`
}
